import {
  EntityRefUtil,
  formatDateWithUserTimezone,
  formatTimeWithUserTimezone,
  FormType,
  GetUnitReadingsRequestDTO,
  GetUnitReadingsResponseDTO,
  RecordState,
  UnitReadingSource,
} from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';

const columnHelper = createColumnHelper<GetUnitReadingsResponseDTO['data'][number]>();

export const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className="flex w-full justify-center">
        <label className="-m-2 p-2">
          <Checkbox checked={table.getIsAllPageRowsSelected()} onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)} aria-label="Select all" />
        </label>
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex w-full justify-center">
        <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
        </label>
      </div>
    ),
    meta: {
      cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
    },
    enableSorting: false,
    enableHiding: false,
  }),
  columnHelper.accessor('recordedAt', {
    header: 'Created Date',
    cell: ({ row }) => {
      const recordedAt = row.original.recordedAt;
      const userTimezone = useUserStore.getState().user?.timezone;
      return <span>{formatDateWithUserTimezone(new Date(recordedAt), userTimezone)}</span>;
    },
    enableSorting: true,
  }),
  columnHelper.display({
    id: 'createdTime',
    header: 'Created Time',
    cell: ({ row }) => {
      const recordedAt = row.original.recordedAt;
      const userTimezone = useUserStore.getState().user?.timezone;
      return <span>{formatTimeWithUserTimezone(new Date(recordedAt), userTimezone)}</span>;
    },
    enableSorting: true,
  }),
  columnHelper.accessor('unit', {
    header: 'Unit',
    cell: ({ getValue }) => {
      const unit = getValue();
      return <span className="truncate">{unit?.name}</span>;
    },
    enableSorting: true,
    enableColumnFilter: true,
    meta: {
      filterType: 'async-multi-select',
      getFilterOptionsFn: async (inputValue, value) => {
        const filters = inputValue ? { filters: { search: inputValue } } : undefined;
        const { data } = await (await ApiService.GetUnits({ limit: 1000, ...filters, filters: { state: RecordState.Active } })).json();

        const resultsMap = new Map(data.map((unit) => [unit.id, { label: unit.name, value: unit.id }]));

        if (value?.length) {
          const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
          if (missingIds.length) {
            const { data: missingUnits } = await (
              await ApiService.GetUnits({
                limit: 1000,
                filters: { fields: { id: missingIds }, state: RecordState.Active },
              })
            ).json();

            for (const unit of missingUnits) {
              resultsMap.set(unit.id, { label: unit.name, value: unit.id });
            }
          }
        }

        return Array.from(resultsMap.values());
      },
    },
  }),
  columnHelper.accessor('reading', {
    header: 'Unit Reading',
    cell: ({ getValue, row }) => {
      const reading = getValue();
      const unit = row.original.unit;

      return (
        <span>
          {reading.toLocaleString('en-US', { maximumFractionDigits: 2 })} <span className="text-secondary-text">{unit?.abbreviation}</span>
        </span>
      );
    },
    enableSorting: false,
  }),
  columnHelper.accessor('source', {
    header: 'Source',
    cell: ({ getValue, row }) => {
      const source = getValue();
      const form = row.original.form;

      return (
        <Badge
          variant={match(source)
            .with(UnitReadingSource.Manual, () => 'success' as const)
            .with(UnitReadingSource.Form, () => {
              if (!form) return 'default' as const;
              return match(form.type)
                .with(FormType.InspectionForm, () => 'default' as const)
                .with(FormType.RepairForm, () => 'orange' as const)
                .with(FormType.ScheduleForm, () => 'violet' as const)
                .otherwise(() => 'default' as const);
            })
            .exhaustive()}
          className="max-w-[150px] capitalize xl:max-w-full">
          <span className="truncate">
            {match(source)
              .with(UnitReadingSource.Manual, () => 'Manual' as const)
              .with(UnitReadingSource.Form, () => form?.name || ('Form' as const))
              .exhaustive()}
          </span>
        </Badge>
      );
    },
  }),
  columnHelper.display({
    id: 'createdBy',
    header: 'Created By',
    cell: ({ row }) => {
      const user = row.original.user;
      if (!user) return <span className="text-secondary-text">-</span>;

      const value = EntityRefUtil.createUserFullNameRef(user, '-');
      const avatarBackgroundColorClass = getAvatarBackgroundColorClass(user?.firstName || '');
      return (
        <UserAvatarLabel
          value={value || '-'}
          operatedByFullInitials={initials(value || '-')}
          avatarUrl={user?.avatarUrl || ''}
          avatarBackgroundColorClass={avatarBackgroundColorClass}
        />
      );
    },
    meta: {
      cellClassName: 'w-[120px]',
    },
    enableSorting: false,
  }),
] as AccessorKeyColumnDef<GetUnitReadingsResponseDTO['data'][number]>[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<NonNullable<typeof columns>[number]['id']> | NonNullable<NonNullable<typeof columns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetUnitReadingsRequestDTO['filters']>['fields']>
> = {
  unit: 'unitId',
  source: 'source',
};
